import {
  helpers,
  sameAs,
  email as vuelidateEmail,
  required as vuelidateRequired,
} from '@vuelidate/validators';
import moment from 'moment';
import { CARD_TYPES } from '../models/card-type';

const REQUIRED_MESSAGE = 'This field is required';
const REQUIRED_SHORT_MESSAGE = 'Required';
const INVALID_EMAIL = 'Invalid email address';

const required = helpers.withMessage(REQUIRED_MESSAGE, vuelidateRequired);
const requiredWithShortMessage = helpers.withMessage(REQUIRED_SHORT_MESSAGE, vuelidateRequired);
const email = helpers.withMessage(INVALID_EMAIL, vuelidateEmail);

const requiredTrue = helpers.withMessage(REQUIRED_MESSAGE, sameAs(true));
const validDate = helpers.withMessage('Please enter a valid date', val => moment(val).isValid());
const validOptionalDate = helpers.withMessage(
  'Please enter a valid date',
  val => val == null || moment(val).isValid(),
);

const validCreditCardName = helpers.withMessage(
  'Please enter a valid name',
  val => {
    if (val == null) {
      return true;
    }
    const regex = /^[a-zA-Z]+(?:[ \-'][a-zA-Z]+)*$/;
    return regex.test(val);
  },
);

/**
 * Default external validation error. Used if there is no validation on client-side, only on server.
 * If we don't use this, the error won't appear.
 * Using $autoDirty to automatically mark the field as dirty and reset external error value.
 * @returns {object}
 */
const externalServerValidation = { external: () => true, $autoDirty: true };

/**
 * Check if group name valid or not.
 * @param {string} name Credit card name.
 * @returns {boolean}
 */
const groupNameValid = name => {
  const regex = /^[A-Za-z0-9_\-\s.]+$/;
  return regex.test(name);
};

/**
 * Validate DOB.
 * @param {string} text Text to check.
 * @returns {boolean}
 */
const validDateOfBirthValidator = text => {
  const dob = moment(text); // strict parsing
  if (!dob.isValid()) return false;

  const today = moment();
  const age = today.diff(dob, 'years');

  return age >= 14;
};

const validDateOfBirth = helpers.withMessage(
  'Student must be 14 or older ',
  validDateOfBirthValidator,
);

/**
 * No number validator.
 * @param {string} text Text to check.
 * @returns {boolean}
 */
const noNumbersValidator = text => {
  // eslint-disable-next-line no-control-regex
  const regex = /^[A-Za-z]+$/;
  return regex.test(text);
};

const noNumbers = helpers.withMessage(
  'Invalid character(s)',
  noNumbersValidator,
);

/**
 * Check text meet ascii rule.
 * @param {string} text Text to check.
 * @returns {boolean}
 */
const validText = text => {
  // eslint-disable-next-line no-control-regex
  const regex = /[^\x00-\x7F]/;
  return !regex.test(text);
};

/**
 * Basic credit card validation
 * Note: The legacy validate function that existed in this project and is used paymentForm.vue
 * @param {string} rawCardNumber Card number, optionally separated by spaces or dashes.
 * @returns {boolean}
 */
const creditCardValid = rawCardNumber => {
  const cardTypes = [...CARD_TYPES];

  if (typeof rawCardNumber !== 'string') {
    return false;
  }

  // Strip allowed separators only, so any other character still fails the digit check below
  const cardNumber = rawCardNumber.replace(/[\s-]/g, '');

  // Ensure that the user has provided a credit card number
  if (cardNumber.length === 0) {
    return false;
  }

  // Check that the number is numeric
  if (!/^[0-9]{13,19}$/.exec(cardNumber)) {
    return false;
  }

  let cardIsValid = false;

  cardTypes.forEach(cardType => {
    // See if the prefix is valid for this card
    let prefixValid = false;
    let prefix = [];
    prefix = cardType.prefixes.split(',');
    for (let i = 0; i < prefix.length; i++) {
      const exp = new RegExp(`^${prefix[i]}`);
      if (exp.test(cardNumber)) {
        prefixValid = true;
      }
    }
    if (!prefixValid) {
      return;
    }

    // See if the length is valid for this card
    let lengthValid = false;
    let lengths = [];
    lengths = cardType.length.split(',');
    for (let j = 0; j < lengths.length; j++) {
      if (cardNumber.length === Number(lengths[j])) {
        lengthValid = true;
      }
    }
    if (!lengthValid) {
      return;
    }

    cardIsValid = true;
  });

  return cardIsValid;
};

/**
 * Validate future date.
 * @param {string} month Month to check.
 * @param {string} year Year to check.
 * @returns {boolean}
 */
function validFutureDate(month, year) {
  const [currentMonth, currentYear] = moment().format('MM YYYY').split(' ');
  let monthValid = true;
  if (year.toString() === currentYear) {
    monthValid = month > currentMonth;
  }
  const yearValid = year >= currentYear;
  return monthValid && yearValid;
}
export const appValidator = {
  requiredTrue,
  required,
  requiredWithShortMessage,
  validDate,
  validOptionalDate,
  externalServerValidation,
  creditCardValid,
  groupNameValid,
  validText,
  email,
  noNumbers,
  validFutureDate,
  validCreditCardName,
  validDateOfBirth,
};
